
import { supabase } from "../../integrations/supabase/client";
import { User } from "../../types/auth";

/**
 * Uploads company logo to storage
 */
export const uploadCompanyLogo = async (file: File, userEmail: string): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `company-logo-${userEmail}-${Date.now()}.${fileExt}`;
  
  const { error: uploadError, data } = await supabase.storage
    .from('company-logos')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: true
    });
    
  if (uploadError) {
    throw uploadError;
  }
  
  const { data: { publicUrl } } = supabase.storage
    .from('company-logos')
    .getPublicUrl(fileName);
    
  return publicUrl;
};

/**
 * Fetches the user profile
 */
export const getUserProfile = async (userId: string): Promise<User | null> => {
  const { data: userData, error: userError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
    
  if (userError) {
    console.error("Error fetching user profile:", userError);
    return null;
  }
  
  return {
    id: userId,
    email: '', // Email is not stored in the profile
    name: userData?.name || '',
    companyName: userData?.company_name,
    companyLogo: userData?.company_logo,
    profileImage: userData?.profile_image,
    // Add the new fields
    phone: userData?.phone,
    address: userData?.address,
    website: userData?.website,
    document: userData?.document
  };
};

/**
 * Updates a user's profile
 */
export const updateUserProfile = async (
  userId: string, 
  updates: {
    name?: string;
    profileImage?: string | File;
    companyName?: string;
    companyLogo?: string | File;
    // Additional fields that might be in the profiles table
    phone?: string;
    address?: string;
    website?: string;
    document?: string;
  }
) => {
  // Prepare update data
  const updateData: any = {
    updated_at: new Date().toISOString()
  };
  
  // Handle text fields
  if (updates.name) updateData.name = updates.name;
  if (updates.companyName) updateData.company_name = updates.companyName;
  if (updates.phone) updateData.phone = updates.phone;
  if (updates.address) updateData.address = updates.address;
  if (updates.website) updateData.website = updates.website;
  if (updates.document) updateData.document = updates.document;
  
  // Handle profile image upload if it's a File
  if (updates.profileImage && updates.profileImage instanceof File) {
    try {
      const file = updates.profileImage;
      const fileExt = file.name.split('.').pop();
      const fileName = `profile-${userId}-${Date.now()}.${fileExt}`;
      
      // Upload the file to the profiles bucket
      const { error: uploadError, data } = await supabase.storage
        .from('profiles')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (uploadError) throw uploadError;
      
      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('profiles')
        .getPublicUrl(fileName);
        
      updateData.profile_image = publicUrl;
    } catch (error) {
      throw new Error("Failed to upload profile image");
    }
  } else if (typeof updates.profileImage === 'string') {
    updateData.profile_image = updates.profileImage;
  }
  
  // Handle company logo upload if it's a File
  if (updates.companyLogo && updates.companyLogo instanceof File) {
    try {
      const file = updates.companyLogo;
      const fileExt = file.name.split('.').pop();
      const fileName = `company-${userId}-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(fileName);
        
      updateData.company_logo = publicUrl;
    } catch (error) {
      console.error("Error uploading company logo:", error);
      throw new Error("Failed to upload company logo");
    }
  } else if (typeof updates.companyLogo === 'string') {
    updateData.company_logo = updates.companyLogo;
  }
  
  // Update the profile in Supabase
  const { error } = await supabase
    .from('profiles')
    .update(updateData)
    .eq('id', userId);
    
  if (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
  
  // Return the updated profile
  return getUserProfile(userId);
};
